import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getStatus, submitAttendance } from "../api";
import { isMobileDevice } from "../utils";

const FIELDS = [
  { key: "full_name",     label: "Full Name",         type: "text",  placeholder: "Riya Sharma",        validate: v => v.trim().length < 2 ? "Enter your full name." : "" },
  { key: "email",         label: "Email Address",     type: "email", placeholder: "riya@amity.edu",   validate: v => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? "Enter a valid email address." : "" },
  { key: "phone",         label: "Phone Number",      type: "tel",   placeholder: "+91 98765 43210",    validate: v => !/^\+?[\d\s\-]{8,15}$/.test(v.trim()) ? "Enter a valid phone number." : "" },
  { key: "enrollment_no", label: "Enrollment Number", type: "text",  placeholder: "A2345922026",          validate: v => v.trim().length < 3 ? "Enter your enrollment number." : "" },
  { key: "department",    label: "Department",        type: "text",  placeholder: "Computer Science",   validate: v => v.trim().length < 2 ? "Enter your department." : "" },
  { key: "course",        label: "Course",            type: "text",  placeholder: "B.Tech",             validate: v => v.trim().length < 2 ? "Enter your course." : "" },
  { key: "batch",         label: "Batch / Year",      type: "text",  placeholder: "2022–2026",          validate: v => v.trim().length < 2 ? "Enter your batch." : "" },
];

function getSubmittedKey(eventName) {
  return `submitted:${eventName}`;
}

// ── Geolocation — works on iOS Safari too ─────────────────────────────────────
function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    // iOS Safari requires a user-gesture and HTTPS — both satisfied here
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude:  parseFloat(pos.coords.latitude.toFixed(6)),
        longitude: parseFloat(pos.coords.longitude.toFixed(6)),
        accuracy:  Math.round(pos.coords.accuracy), // metres
      }),
      () => resolve(null), // user denied or timed out — don't block submission
      {
        enableHighAccuracy: true,
        timeout: 10000,      // 10s — iOS can be slow to get GPS lock
        maximumAge: 0,       // always fresh, no cached position
      }
    );
  });
}

export default function AttendPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const isMobile = isMobileDevice();

  // "checking" | "desktop_blocked" | "location_prompt" | "location_denied" | "valid" | "invalid" | "already_submitted" | "success"
  const [pageState, setPageState] = useState("checking");
  const [eventName, setEventName] = useState("");
  const [form, setForm] = useState({});
  const [feedback, setFeedback] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [feedbackError, setFeedbackError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [location, setLocation] = useState(null);         // captured coords
  const [locationStatus, setLocationStatus] = useState("idle"); // "idle"|"fetching"|"granted"|"denied"

  useEffect(() => {
    if (!isMobile) { setPageState("desktop_blocked"); return; }

    getStatus()
      .then(s => {
        if (!s.is_open || s.token !== token) { setPageState("invalid"); return; }
        setEventName(s.event_name);
        if (localStorage.getItem(getSubmittedKey(s.event_name))) {
          setPageState("already_submitted");
        } else {
          setPageState("location_prompt");
        }
      })
      .catch(() => setPageState("invalid"));
  }, [token, isMobile]);

  // Ask for location — called when user taps "Allow & Continue"
  const requestLocation = async () => {
    setLocationStatus("fetching");
    const coords = await getLocation();
    if (coords) {
      setLocation(coords);
      setLocationStatus("granted");
      setPageState("valid");
    } else {
      setLocationStatus("denied");
      setPageState("location_denied"); // blocked — location is mandatory
    }
  };

  const handleChange = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    // Clear field error on change
    if (fieldErrors[key]) setFieldErrors(e => ({ ...e, [key]: "" }));
  };

  const handleSubmit = async () => {
    // Validate all fields
    const errors = {};
    for (const f of FIELDS) {
      const err = f.validate(form[f.key] || "");
      if (err) errors[f.key] = err;
    }
    // Validate feedback (now required)
    const fbErr = feedback.trim().length < 5 ? "Please share at least a few words of feedback." : "";
    if (fbErr) setFeedbackError(fbErr);
    else setFeedbackError("");

    if (Object.keys(errors).length > 0 || fbErr) {
      setFieldErrors(errors);
      setErrorMsg("Please fix the errors above before submitting.");
      return;
    }

    setErrorMsg("");
    setSubmitting(true);

    // If location not yet fetched, try one more time silently
    let coords = location;
    if (!coords && locationStatus === "idle") {
      coords = await getLocation();
      if (coords) setLocation(coords);
    }

    try {
      await submitAttendance({
        ...form,
        feedback,
        token,
        location: coords || null,
      });
      localStorage.setItem(getSubmittedKey(eventName), "1");
      setPageState("success");
    } catch (e) {
      setErrorMsg(e.message || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Page states ──────────────────────────────────────────────────────────────

  if (pageState === "checking") return <Screen message="Verifying…" />;

  if (pageState === "desktop_blocked") return (
    <Screen icon="📱" title="Mobile Only"
      message="Attendance can only be marked from a mobile device. Please scan the QR code with your phone."
      showHome />
  );

  if (pageState === "invalid") return (
    <Screen icon="✕" title="Invalid Link"
      message="This attendance link is invalid or has expired."
      showHome />
  );

  if (pageState === "already_submitted") return (
    <Screen icon="✓" title="Already Submitted"
      message="You have already marked your attendance for this event."
      accent showHome />
  );

  if (pageState === "success") return (
    <Screen icon="✓" title="Attendance Marked!"
      message="Your attendance has been recorded successfully."
      accent showHome />
  );

  // ── Location denied — mandatory, so block form ───────────────────────────────

  if (pageState === "location_denied") return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: "center", maxWidth: 360, padding: "2.5rem 2rem" }}>
        <p style={{ fontSize: "2.2rem", margin: "0 0 1rem" }}>🚫</p>
        <h2 style={{ ...s.heading, marginBottom: "0.6rem" }}>Location Required</h2>
        <p style={{ color: "#999", fontSize: "0.85rem", lineHeight: 1.7, margin: "0 0 0.8rem" }}>
          Location access is mandatory to mark attendance. Please enable it and try again.
        </p>
        <p style={{ color: "#bbb", fontSize: "0.75rem", lineHeight: 1.7, margin: "0 0 1.8rem" }}>
          <strong style={{ color: "#111" }}>iPhone:</strong> Settings → Privacy & Security → Location Services → enable for your browser.<br />
          <strong style={{ color: "#111" }}>Android:</strong> Settings → Apps → Browser → Permissions → Location → Allow.
        </p>
        <button onClick={requestLocation} style={s.btn(true)}>
          Try Again
        </button>
      </div>
    </div>
  );

  // ── Location prompt ──────────────────────────────────────────────────────────

  if (pageState === "location_prompt") return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: "center", maxWidth: 360, padding: "2.5rem 2rem" }}>
        <p style={{ fontSize: "2.2rem", margin: "0 0 1rem" }}>📍</p>
        <h2 style={{ ...s.heading, marginBottom: "0.6rem" }}>Allow Location Access</h2>
        <p style={{ color: "#999", fontSize: "0.85rem", lineHeight: 1.7, margin: "0 0 0.6rem" }}>
          Your location will be recorded along with your attendance to verify you are physically present.
        </p>
        <p style={{ color: "#bbb", fontSize: "0.75rem", lineHeight: 1.6, margin: "0 0 1.8rem" }}>
          When your browser asks — tap <strong style={{ color: "#111" }}>Allow</strong>.
          On iPhone, make sure Location is enabled for your browser in Settings → Privacy → Location Services.
        </p>
        <button onClick={requestLocation} style={s.btn(true)}>
          Allow &amp; Continue
        </button>
      </div>
    </div>
  );

  // ── Form ─────────────────────────────────────────────────────────────────────

  return (
    <div style={s.page}>
      <div style={s.card}>
        <button onClick={() => navigate("/")} style={s.back}>← Back</button>
        <h2 style={s.heading}>Mark Attendance</h2>
        <p style={s.sub}>All fields are required.</p>

        {/* Location status indicator */}
        {locationStatus !== "idle" && (
          <div style={s.locationBadge(locationStatus)}>
            {locationStatus === "fetching" && "📍 Getting location…"}
            {locationStatus === "granted"  && `📍 Location captured (±${location?.accuracy}m)`}
            {locationStatus === "denied"   && "📍 Location not recorded"}
          </div>
        )}

        <div style={s.fields}>
          {FIELDS.map(f => (
            <div key={f.key} style={s.group}>
              <label style={s.label}>{f.label}</label>
              <input
                type={f.type}
                placeholder={f.placeholder}
                value={form[f.key] || ""}
                onChange={e => handleChange(f.key, e.target.value)}
                style={{ ...s.input, ...(fieldErrors[f.key] ? s.inputError : {}) }}
              />
              {fieldErrors[f.key] && (
                <span style={s.fieldErr}>{fieldErrors[f.key]}</span>
              )}
            </div>
          ))}

          {/* Feedback — now required */}
          <div style={s.group}>
            <label style={s.label}>Session Feedback</label>
            <textarea
              placeholder="Share your thoughts about this session…"
              value={feedback}
              onChange={e => {
                setFeedback(e.target.value);
                if (feedbackError) setFeedbackError("");
              }}
              rows={3}
              style={{ ...s.input, resize: "vertical", ...(feedbackError ? s.inputError : {}) }}
            />
            {feedbackError && <span style={s.fieldErr}>{feedbackError}</span>}
          </div>
        </div>

        {errorMsg && <p style={s.error}>{errorMsg}</p>}

        <button onClick={handleSubmit} disabled={submitting} style={s.btn(!submitting)}>
          {submitting ? "Submitting…" : "Submit Attendance"}
        </button>

        <p style={s.footer}>
          Developed by{" "}
          <a
            href="https://www.linkedin.com/in/rajatrajputdev"
            target="_blank"
            rel="noreferrer"
            style={s.footerLink}
          >
            Rajat Rajput
          </a>
        </p>
      </div>
    </div>
  );
}

function Screen({ icon, title, message, accent, showHome }) {
  const navigate = useNavigate();
  return (
    <div style={s.page}>
      <div style={{ ...s.card, textAlign: "center", maxWidth: 380, padding: "3rem 2rem" }}>
        {icon && <p style={{ fontSize: "2.5rem", margin: "0 0 1rem", color: accent ? "#16a34a" : "#ccc" }}>{icon}</p>}
        {title && <h2 style={{ ...s.heading, marginBottom: "0.5rem" }}>{title}</h2>}
        <p style={{ color: "#999", fontSize: "0.88rem", lineHeight: 1.6 }}>{message}</p>
        {showHome && (
          <button onClick={() => navigate("/")} style={{ ...s.btn(true), marginTop: "2rem" }}>
            Go Home
          </button>
        )}
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f5f5f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'DM Mono', monospace",
    padding: "1.5rem",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: "2rem 1.8rem",
    maxWidth: 480,
    width: "100%",
    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
  },
  back: {
    background: "none", border: "none", color: "#bbb", fontSize: "0.82rem",
    cursor: "pointer", padding: 0, marginBottom: "1.5rem",
    letterSpacing: "0.04em", fontFamily: "'DM Mono', monospace",
  },
  heading: { fontSize: "1.6rem", fontWeight: 700, color: "#111", margin: 0, letterSpacing: "-0.02em" },
  sub: { color: "#bbb", fontSize: "0.75rem", margin: "0.3rem 0 1.5rem", letterSpacing: "0.08em", textTransform: "uppercase" },
  locationBadge: (status) => ({
    display: "inline-block",
    padding: "5px 10px",
    borderRadius: "4px",
    fontSize: "0.72rem",
    marginBottom: "1.2rem",
    background: status === "granted" ? "#f0fdf4" : status === "denied" ? "#fafafa" : "#fffbeb",
    color:      status === "granted" ? "#16a34a" : status === "denied" ? "#bbb"    : "#b45309",
    border:     `1px solid ${status === "granted" ? "#bbf7d0" : status === "denied" ? "#e5e5e5" : "#fde68a"}`,
  }),
  fields: { display: "flex", flexDirection: "column", gap: "1.1rem" },
  group: { display: "flex", flexDirection: "column", gap: 4 },
  label: { color: "#888", fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase" },
  input: {
    background: "#fafafa", border: "1px solid #e8e8e8", borderRadius: "5px",
    color: "#111", padding: "10px 12px", fontSize: "0.9rem",
    fontFamily: "'DM Mono', monospace", outline: "none",
  },
  inputError: { borderColor: "#fca5a5", background: "#fff8f8" },
  footer: {
    marginTop: "2rem",
    textAlign: "center",
    color: "#ccc",
    fontSize: "0.72rem",
    letterSpacing: "0.04em",
  },
  footerLink: {
    color: "#888",
    textDecoration: "none",
    fontWeight: 600,
    borderBottom: "1px solid #e0e0e0",
  },
  fieldErr: { color: "#dc2626", fontSize: "0.72rem", marginTop: 2 },
  error: { color: "#dc2626", fontSize: "0.8rem", margin: "0.8rem 0 0" },
  btn: (active) => ({
    width: "100%", marginTop: "1.8rem", padding: "14px",
    background: active ? "#111" : "#f0f0f0",
    color: active ? "#fff" : "#bbb", border: "none", borderRadius: "6px",
    fontSize: "0.9rem", fontWeight: 700, fontFamily: "'DM Mono', monospace",
    cursor: active ? "pointer" : "not-allowed", letterSpacing: "0.04em", textTransform: "uppercase",
  }),

};